// This is the web SDK. It is used by the launcher/customer JS code to communicate with the chat component (in cross-site iFrame).
// The iFrame does 2 things:
// 1. Emit events to the parent window (launcher) when the chat component is ready, when the chat component sends a message, etc.
// 2. Receives and processes messages from the parent window (launcher) via the call() interface.
// 3. Customers use the SDK to easily interact with both the launcher and the chat component, which hides the async nature of the iFrame communication.
//
// Clients can access the SDK as $botbrains in any part of their JS code.
// Unless the SDK is initialized, only the .push function is available.
// We use push for functions that do not return anything, to allow them to be async.
import TagManager from "./chat/tags";
import { setConfig, setLocale, setMode, setTheme } from "./slices/configSlice";
import {
  setIsPreviewVisible,
  setOpen,
  setShow,
  toggleLauncher,
  setSideMargin,
  setBottomMargin,
  setSide,
} from "./slices/launcherSlice";
import {
  setPrompt,
  setQuickReplies,
  setSuggestions,
} from "./slices/promptSlice";
import { trigger } from "./slices/signalSlice";
import { updateUserDataAndPing, UserData } from "./slices/userSlice";
import { hydrateEntireStore, resetEntireStore, store } from "./store";
import { ConfigData, ConfigTheme } from "./types/core";
import { StateJson, stateJsonToState } from "./editorState";
import { pingFrameWithCurrentState } from "./actions/sharedActions";
import { resetLocalStorage } from "./hooks";
import { clearStorage } from "./storage";
import whenStateReaches from "./whenStateReaches";
import {
  appendInitialMessages,
  postponeRating,
  setOverrides,
} from "./slices/chatSlice";
import { getEngagementActions } from "./actions/engage";

type QueueItem = [string, string, any | undefined];

interface SDK {
  push(item: QueueItem): void;
  get(method: string): Promise<any>;
  is(method: string): boolean;
}

type Callback = (...args: any[]) => void;

export class Impl implements SDK {
  private eventHandlers: { [key: string]: Callback[] } = {};
  private actionQueue: QueueItem[] = [];
  // isLoaded means the SDK is ready to be called by clients
  // will execute most actions immediately
  private isEnabled: boolean = false;
  // isComponentReady means that all commands can be executed
  private isComponentReady: boolean = false;

  public doMap: Map<string, (params: any | undefined) => void>;
  public isMap: Map<string, () => boolean>;
  public setMap: Map<string, (params: any | undefined) => void>;
  public getMap: Map<string, (key: string) => Promise<any>>;

  constructor() {
    this.doMap = new Map<string, (params?: any) => void>();
    this.isMap = new Map<string, () => boolean>();
    this.setMap = new Map<string, (params: any | undefined) => void>();
    this.getMap = new Map<string, (key: string) => Promise<any>>();

    this.isMap.set("chat.ready", () => this.isComponentReady);
    this.on("frame.loaded", () => this.onReady());
  }

  private processQueue(operations?: string[]): void {
    this.log("Processing queue... size: " + this.actionQueue.length);
    if (typeof operations == "undefined") {
      operations = ["on", "off", "is", "do", "set", "get"];
    }
    const queue = this.actionQueue.filter((item) =>
      operations.includes(item[0])
    );
    this.actionQueue = this.actionQueue.filter(
      (item) => !operations.includes(item[0])
    );
    while (queue.length > 0) {
      const item = queue.shift();
      if (item === undefined) continue;
      this.log(item.toString());
      if (item) {
        this.executeAction(item);
      }
    }
  }

  // push() should be called by customers. If in doubt, use .push().
  push(item: QueueItem): void {
    if (this.isComponentReady) {
      this.executeAction(item);
    } else if (["on", "off"].includes(item[0])) {
      this.executeAction(item);
    } else {
      if (["get"].includes(item[0]))
        throw new Error(`Action ${item[0]} not allowed in .push API`);
      this.actionQueue.push(item);
    }
  }

  private executeAction(item: QueueItem): void {
    this.log(`Executing action: ${item}`);
    const [action, method, params] = item;
    switch (action) {
      case "set":
        this.set(method, params);
        break;
      case "do":
        this.do(method, params);
        break;
      case "on":
        this.on(method, params);
        break;
      case "off":
        this.off(method, params);
        break;
      default:
        console.error(`Unknown action: ${action}`);
    }
  }

  // set() may be called by customers. Prefer .push().
  set(method: string, params: any | undefined): void {
    const action = this.setMap.get(method);
    if (!action) {
      throw new Error(`Unknown method: ${method}`);
    }
    action(params);
  }

  // do() may be called by customers. Prefer .push().
  do(method: string, params: any | undefined = undefined): void {
    this.ensureEnabled();
    const action = this.doMap.get(method);
    if (!action) {
      throw new Error(`Unknown method: ${method}`);
    }
    action(params);
  }

  // get() should be called by customers.
  get(key: string): Promise<any> {
    this.ensureReady();
    const action = this.getMap.get(key);
    if (!action) {
      throw new Error(`Unknown key: ${key}`);
    }
    return action(key);
  }

  // is checks if a predicate referenced by name is true.
  // is() should be called by customers.
  is(predicateName: string): boolean {
    this.ensureEnabled();
    const predicate = this.isMap.get(predicateName);
    if (!predicate) {
      throw new Error(`Unknown predicate: ${predicateName}`);
    }
    return predicate();
  }

  // on registers an event handler for a given event
  // idempotent for same callback identity
  // on() may be called by customers.
  on(event: string, callback: Callback): void {
    if (!this.eventHandlers[event]) {
      this.eventHandlers[event] = [];
    }
    if (!this.eventHandlers[event].includes(callback)) {
      this.eventHandlers[event].push(callback);
    }
  }

  // off unregisters an event handler for a given event
  // idempotent for same callback identity
  // off() may be called by customers.
  off(event: string, callback: Callback): void {
    if (this.eventHandlers[event]) {
      this.eventHandlers[event] = this.eventHandlers[event].filter(
        (cb) => cb !== callback
      );
    }
  }

  // emit() should NOT be called by customers.
  emit(event: string, properties?: any): void {
    this.log(`SDK-Event:  ${event}`);
    if (this.eventHandlers[event]) {
      this.eventHandlers[event].forEach((cb) => cb(properties));
    }
  }

  // enable means all actions are registered, but may not be executed yet because the SDK is not ready.
  // This means that .push, .is, .on, .off, .set are fully available and executable.
  // .get and .do calls may or may not be executed, push() for async dispatch (or .is("chat.ready")).
  enable(): void {
    if (this.isEnabled) return;
    this.isEnabled = true;
    this.log("Loaded botBrains SDK...");
    if (window.BOTBRAINS_ON_LOADED) {
      this.log("Loaded callback found, executing now...");
      window.BOTBRAINS_ON_LOADED();
    }
    this.processQueue(["is", "on", "off", "set"]);
  }

  help(): string[] {
    function getParamSignature(func: Function) {
      const funcStr = func.toString();
      const hasArgs = !funcStr.startsWith("()");
      return hasArgs ? `, args` : "";
    }

    const methods = [];

    // Function call notation
    methods.push(
      ...this.doMap
        .entries()
        .map(([key, fn]) => `do('${key}'${getParamSignature(fn)})`)
    );

    methods.push(
      ...this.setMap
        .entries()
        .map(([key, fn]) => `set('${key}'${getParamSignature(fn)})`)
    );

    methods.push(...this.isMap.entries().map(([key]) => `is('${key}')`));

    methods.push(...this.getMap.entries().map(([key]) => `get('${key}')`));

    // TODO(liamvdv): formalize supported events.
    const events = ["chat.ready", "frame.loaded", "config.loaded"];
    methods.push(...events.map((e) => `on('${e}', callback)`));
    methods.push(...events.map((e) => `off('${e}', callback)`));
    methods.push("push([action, method, args])");

    return methods.map((m) => `$botbrains.${m}`);
  }

  private ensureEnabled(): void {
    if (!this.isEnabled) throw new Error("SDK not loaded yet");
  }

  // onReady means all actions can be executed (Frame was fetched)
  private onReady(): void {
    if (this.isComponentReady) return;
    this.log("Readying botBrains SDK...");
    this.isComponentReady = true;
    this.processQueue();
    this.emit("chat.ready", undefined);
  }

  private ensureReady(): void {
    if (!this.isComponentReady) throw new Error("SDK not ready yet");
  }

  private log(message: string): void {
    if (!window.BOTBRAINS_DEBUG) return;
    console.log(message);
  }
}

// Never import sdk directly, thus void.
export function mountSdk(): Impl {
  const sdk = new Impl();
  if (typeof window.$botbrains !== "object") {
    throw new Error("window.$botbrains is not available");
  } else {
    if (Array.isArray(window.$botbrains)) {
      window.$botbrains.forEach((item) => sdk.push(item));
      window.$botbrains = sdk;
    }
  }
  return window.$botbrains;
}

export function isValidExternalId(unsafe: string): [boolean, string] {
  const invalidStringsCaseInsensitive = [
    "anonymous",
    "guest",
    "distinctid",
    "distinct_id",
    "id",
    "not_authenticated",
    "email",
    "undefined",
    "true",
    "false",
  ];

  const invalidStringsCaseSensitive = [
    "[object Object]",
    "NaN",
    "None",
    "none",
    "null",
    "0",
  ];

  // Check for empty space strings
  if (unsafe.trim().length === 0) {
    return [false, "External ID cannot be an empty space string"];
  }

  // Check for case insensitive invalid strings
  if (invalidStringsCaseInsensitive.includes(unsafe.toLowerCase())) {
    return [
      false,
      `External ID cannot be one of the following (case insensitive): ${invalidStringsCaseInsensitive.join(", ")}`,
    ];
  }

  // Check for case sensitive invalid strings
  if (invalidStringsCaseSensitive.includes(unsafe)) {
    return [
      false,
      `External ID cannot be one of the following (case sensitive): ${invalidStringsCaseSensitive.join(", ")}`,
    ];
  }

  // If all checks pass, return true
  return [true, ""];
}

// late bind store
export function registerSdkCommands(sdk: Impl): void {
  // Init - not [set,config,...] because special meaning
  sdk.setMap.set("config", (payload: Partial<ConfigData>) => {
    const config = mergeConfig(payload);
    store.dispatch(setConfig(config));
    sdk.emit("config.loaded");
  });

  sdk.getMap.set("config", () => Promise.resolve(store.getState().config.data));

  // Access and Control Visibility/Openness
  sdk.doMap.set("chat.show", () => store.dispatch(setShow(true)));
  sdk.doMap.set("chat.hide", () => store.dispatch(setShow(false)));
  sdk.doMap.set("chat.open", () => store.dispatch(setOpen(true)));
  sdk.doMap.set("chat.close", () => store.dispatch(setOpen(false)));
  sdk.doMap.set("chat.toggle", () => store.dispatch(toggleLauncher()));

  sdk.doMap.set("__csat.show", () => {
    store.dispatch(postponeRating(0));
  });

  sdk.isMap.set("chat.visible", () => store.getState().launcher.isShow);
  sdk.isMap.set("chat.opened", () => store.getState().launcher.isOpen);

  // Access and Control Previews
  sdk.doMap.set("preview.show", () =>
    store.dispatch(setIsPreviewVisible(true))
  );
  sdk.doMap.set("preview.hide", () =>
    store.dispatch(setIsPreviewVisible(false))
  );
  sdk.isMap.set(
    "preview.visible",
    () => store.getState().launcher.isPreviewVisible
  );

  // Chat positioning and margins
  sdk.setMap.set("chat.marginSide", (payload: string | null) => {
    store.dispatch(setSideMargin(payload));
  });
  sdk.setMap.set("chat.marginBottom", (payload: string | null) => {
    store.dispatch(setBottomMargin(payload));
  });
  sdk.setMap.set("chat.side", (payload: "left" | "right" | null) => {
    if (payload !== null && !["left", "right"].includes(payload)) {
      throw new Error(
        `Invalid side: ${payload}. Must be 'left', 'right', or null`
      );
    }
    store.dispatch(setSide(payload));
  });

  sdk.getMap.set("chat.theme", () => {
    const frameId = store.getState().config.data.frameId;
    const locale = store.getState().config.data.theme;
    const frame = store.getState().frame.data[frameId];
    const localeData = frame.locales.find((l) => l.locale === locale);
    // TODO(liamvdv): this does NOT wait for the frame data to exist. Rewrite to wait for the frame data to exist.
    return Promise.resolve(localeData?.suggestions || []);
  });
  sdk.getMap.set("chat.suggestions", () =>
    whenStateReaches(
      store,
      (state) => state.prompt.suggestions !== undefined,
      (state) => state.prompt.suggestions
    )
  );
  sdk.getMap.set("config.locale", () =>
    whenStateReaches(
      store,
      (state) => state.config.status === "succeeded",
      (state) => state.config.data.locale
    )
  );

  sdk.setMap.set("chat.suggestions", (payload: string[]) => {
    // It is the customers responsibility to ensure that the locale is correct, i. e.
    // if ($botbrains.get("config.locale") === "en") $botbrains.set("chat.suggestions", ["Where to order ABC?", "Help with XYZ?"]);

    // NOTE(liamvdv): we explicitly keep chat.suggestions in the store, instead of overwriting the current api locale, since they are refreshed whenever a message arrives / a user data is annotated, ...
    store.dispatch(setSuggestions(payload));
  });
  // use choices/options/replies/responses over quickreplies?
  sdk.setMap.set("chat.replies", (payload: string[]) => {
    store.dispatch(setQuickReplies(payload));
  });
  sdk.setMap.set("chat.intro", (payload: string) => {
    store.dispatch(
      appendInitialMessages({
        messages: [
          {
            role: "assistant",
            sender: "sdk",
            content: payload,
          },
        ],
        replaceInitialTail: true,
      })
    );
  });

  sdk.setMap.set("message.tags", (payload: Record<string, any>) => {
    // TODO(liamvdv): use redux to store tags / message tags.
    TagManager.setTags(payload);
  });
  sdk.setMap.set("conversation.tags", (payload: Record<string, any>) => {
    // TODO(liamvdv): implement conversation tags
    throw new Error("conversation.tags not implemented");
  });

  sdk.setMap.set("message.text", (payload: string) => {
    store.dispatch(setOpen(true));
    store.dispatch(setPrompt(payload));
  });

  sdk.doMap.set("message.send", (payload: string) => {
    store.dispatch(setOpen(true));
    store.dispatch(setPrompt(payload));
    store.dispatch(trigger("chat.send"));
  });

  sdk.setMap.set("__editorstate", (data: StateJson) => {
    const rootState = stateJsonToState(data);
    store.dispatch(hydrateEntireStore(rootState));
  });

  sdk.setMap.set("__overrides", (payload: any) => {
    store.dispatch(setOverrides(payload));
  });

  sdk.setMap.set("config.theme", (payload: string) => {
    if (!["auto", "light", "dark"].includes(payload)) {
      throw new Error(`Invalid theme: ${payload}`);
    }
    store.dispatch(setTheme(payload as ConfigTheme));
  });

  sdk.setMap.set("config.locale", (payload: string) => {
    store.dispatch(setLocale(payload));
  });

  sdk.setMap.set("config.mode", (payload: ConfigData["mode"]) => {
    if (
      ![
        "launcher",
        "fullscreen",
        "inline",
        "fullscreen_headerless",
        "whatsapp",
      ].includes(payload)
    ) {
      throw new Error(`Invalid mode: ${payload}`);
    }
    store.dispatch(setMode(payload));
  });

  // update identified user
  sdk.setMap.set("user.data", (payload: Partial<UserData>) => {
    payload.external_id = undefined;
    payload.external_id_signature = undefined;
    store.dispatch(updateUserDataAndPing(payload));
  });
  sdk.getMap.set("user.data", () =>
    whenStateReaches(
      store,
      (state) => state.user.status === "succeeded",
      (state) => state.user.data.user_data
    )
  );

  sdk.doMap.set("user.identify", (payload: [any, Partial<UserData>]) => {
    if (payload[1].external_id && payload[1].external_id != payload[0]) {
      throw new Error("External ID cannot be changed");
    }
    const unsafe = String(payload[0]);
    const [ok, reason] = isValidExternalId(unsafe);
    if (!ok) {
      console.error(reason);
      console.log("Ignoring identify, treating as set('user.data')");
      sdk.set("user.data", payload[1]);
    } else {
      const userData = payload[1];
      userData.external_id = unsafe;
      store.dispatch(updateUserDataAndPing(userData));
    }
  });

  // Exit session, keep user.
  // => Cannot retrieve conversation history.
  sdk.doMap.set("session.logout", () => {
    clearStorage(store.getState().config.data.frameId, {
      sessionId: true,
      userId: false,
      deviceId: false,
    });
    const config = store.getState().config.data;
    store.dispatch(resetEntireStore());
    store.dispatch(setConfig(config));
    store.dispatch(
      pingFrameWithCurrentState({
        background: false, // TODO(liamvdv): rethink if this should be foreground
        refresh: true,
      })
    );
  });

  sdk.doMap.set("engage", (engagement_prompt: string) => {
    const actions = store.dispatch(
      getEngagementActions({
        goal: "interaction",
        engagement_prompt,
      })
    );
    actions
      .unwrap()
      .then((r) =>
        r.data.sdk_actions.map((action) => sdk.push(action as QueueItem))
      )
      .catch((error) => {
        console.error("[botbrains] Failed to get engagement actions:", error);
      });
  });

  // => Completely new user.
  sdk.doMap.set("user.logout", () => {
    clearStorage(store.getState().config.data.frameId, {
      sessionId: true,
      userId: true,
      deviceId: false,
    });
    const config = store.getState().config.data;
    store.dispatch(resetEntireStore());
    store.dispatch(setConfig(config));
    store.dispatch(
      pingFrameWithCurrentState({
        background: false, // TODO(liamvdv): rethink if this should be foreground
        refresh: true,
      })
    );
  });

  // Reset the complete app; new user, new session, new device.
  // Keeps config
  sdk.doMap.set("reset", () => {
    clearStorage(store.getState().config.data.frameId, {
      sessionId: true,
      userId: true,
      deviceId: true,
    });
    const config = store.getState().config.data;
    store.dispatch(resetEntireStore());
    store.dispatch(setConfig(config));
    store.dispatch(
      pingFrameWithCurrentState({
        background: false, // TODO(liamvdv): rethink if this should be foreground
        refresh: true,
      })
    );
    resetLocalStorage();
  });
}

function readPreferredLocale(): string {
  // NOTE(liamvdv): navigator.languages is ordered by user preference; navigator.language is the browser install language.
  // https://stackoverflow.com/a/31135571
  if (navigator.languages != undefined) return navigator.languages[0];
  return navigator.language;
}

function mergeConfig(unsafe: Partial<ConfigData>): ConfigData {
  const defaultConfig = {
    apiBase: import.meta.env.VITE_API_ENDPOINT_URL,
    frameId: "",
    cookieDomain: "",
    sessionRefreshLifetime: 6 * 744 * 60, // 6 months
    theme: "auto" as const,
    locale: readPreferredLocale(),
    mode: "launcher" as const,
    selector: "",
    whatsappId: "",
    previewDelayMs: 3000,
    // non-guaranteed experimental* fields.
    experimentalListConversations: false,
  };
  const config = { ...defaultConfig, ...unsafe };
  if (!(config?.frameId && typeof config?.frameId === "string")) {
    throw new Error("[botbrains]: frameId is not a valid string in config");
  }
  if (config.frameId.length != 36) {
    throw new Error(
      `[botbrains]: config.frameId='${config.frameId}' is not a valid UUID. Fix by copying the full frameId from the platform.`
    );
  }

  if (config.mode === "inline") {
    if (!config.selector) {
      throw new Error(
        "[botbrains]: selector is required in config when mode is 'inline'"
      );
    }
    if (typeof config.selector !== "string") {
      throw new Error(
        "[botbrains]: selector must be a string in config when mode is 'inline'"
      );
    }
  } else if (config.mode === "whatsapp") {
    if (!config.whatsappId) {
      throw new Error(
        "[botbrains]: whatsappId is required in config when mode is 'whatsapp'"
      );
    }
    if (typeof config.whatsappId !== "string") {
      throw new Error(
        "[botbrains]: whatsappId must be a string in config when mode is 'whatsapp'"
      );
    }
  } else {
    if (config.selector)
      throw new Error(
        "[botbrains]: selector is not allowed in config when mode is not inline"
      );
  }

  if (!(config?.apiBase && typeof config?.apiBase === "string")) {
    const url = config?.apiBase;
    try {
      new URL(url);
    } catch (e) {
      throw new Error(
        `[botbrains]: apiBase='${url}' is not a valid URL in config: ${e} `
      );
    }
  }

  return config;
}
